import { FC, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useDocuments from '@/api/useDocuments.ts';
import useUploadDocument from '@/api/useUploadDocument.ts';
import useDeleteDocument from '@/api/useDeleteDocument.ts';
import { Button } from 'react-daisyui';
import dayjs from 'dayjs';

const DocumentsPage: FC = () => {
  const { t } = useTranslation();
  const { data: documents, isLoading, isError } = useDocuments();
  const { mutate: uploadDocument, isPending: isUploading } = useUploadDocument();
  const { mutate: deleteDocument } = useDeleteDocument();
  const [selectedType, setSelectedType] = useState<'dbs' | 'insurance' | 'other'>('dbs');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = (type: 'dbs' | 'insurance' | 'other') => {
    setSelectedType(type);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadDocument({
        document_type: selectedType,
        file,
      });
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = (documentId: string) => {
    if (confirm(t('documentsPage.confirmDelete'))) {
      deleteDocument({ document_id: documentId });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'text-[#00A96E]';
      case 'pending':
        return 'text-[#FFBE00]';
      case 'rejected':
        return 'text-[#FF5861]';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'dbs':
        return 'Enhanced DBS';
      case 'insurance':
        return 'Insurance';
      default:
        return 'Other';
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t('documentsPage.title')}</h1>
          <p className="text-gray-600 text-sm mt-1">
            {t('documentsPage.subtitle')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button
            onClick={() => handleUploadClick('dbs')}
            disabled={isUploading}
            className="bg-black text-white border-none hover:opacity-80 rounded-lg px-6 py-2 w-full sm:w-auto"
          >
            {t('documentsPage.uploadDbs')}
          </Button>
          <Button
            onClick={() => handleUploadClick('insurance')}
            disabled={isUploading}
            className="bg-black text-white border-none hover:opacity-80 rounded-lg px-6 py-2 w-full sm:w-auto"
          >
            {t('documentsPage.uploadInsurance')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
          />
        </div>
      </div>

      <div className="">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('documentsPage.loading')}</div>
        ) : isError ? (
          <div className="p-8 text-center text-red-500">{t('documentsPage.errorLoading')}</div>
        ) : !documents || documents.length === 0 ? (
          <div className="bg-[#F6F6F4] rounded-lg flex items-center justify-center py-6 w-full mt-8">
            <p className="text-base font-semibold text-center text-black">
              {t('documentsPage.noDocuments')}
            </p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">{t('documentsPage.document')}</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">{t('documentsPage.dateTime')}</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">{t('common.status')}</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">{t('documentsPage.details')}</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">{t('documentsPage.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-semibold">{getDocumentTypeLabel(doc.document_type)}</td>
                  <td className="px-6 py-4 text-sm">
                    {dayjs(doc.created_at).format('MMMM DD, YYYY — HH:mm A')}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm font-semibold ${getStatusColor(doc.status)}`}>
                      {getStatusLabel(doc.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {doc.signed_url && (
                      <a
                        href={doc.signed_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-black hover:underline font-semibold"
                      >
                        {t('documentsPage.preview')}
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="text-sm text-[#FF5861] hover:opacity-70 font-semibold cursor-pointer"
                    >
                      {t('button.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
